export type StoryTurnPhase = 'prepare' | 'generate' | 'publish'

export class StoryTurnError extends Error {
  constructor(
    readonly phase: StoryTurnPhase,
    cause: unknown
  ) {
    super(cause instanceof Error ? cause.message : String(cause), { cause })
    this.name = 'StoryTurnError'
  }
}

/**
 * The ordinary response lifecycle. Each surface supplies its context preparation,
 * model execution and atomic publication; supporting agents stay inside prepare.
 * Domain operations retain their own cancellation/receipt checks and recovery.
 */
export async function runStoryTurn<Prepared, Generated, Published>(steps: {
  prepare: () => Promise<Prepared>
  generate: (prepared: Prepared) => Promise<Generated>
  publish: (generated: Generated, prepared: Prepared) => Promise<Published>
}): Promise<Published> {
  let phase: StoryTurnPhase = 'prepare'
  try {
    const prepared = await steps.prepare()
    phase = 'generate'
    const generated = await steps.generate(prepared)
    phase = 'publish'
    return await steps.publish(generated, prepared)
  } catch (cause) {
    throw new StoryTurnError(phase, cause)
  }
}
